import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { ANNOUNCEMENT } from "@/data/site";

const STORAGE_KEY = "et_announcement_dismissed";

/**
 * Promotional announcement bar shown above the header.
 * Whole bar is clickable (navigates to ANNOUNCEMENT.linkTo).
 * Dismissible for the browser session (sessionStorage).
 */
export const AnnouncementBar = () => {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!ANNOUNCEMENT.enabled) return;
    const dismissed = ANNOUNCEMENT.dismissible && sessionStorage.getItem(STORAGE_KEY) === "1";
    setVisible(!dismissed);
  }, []);

  if (!ANNOUNCEMENT.enabled || !visible) return null;

  const dismiss = (e) => {
    e.stopPropagation();
    setVisible(false);
    try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch (err) {}
  };

  const go = () => {
    if (ANNOUNCEMENT.linkTo) navigate(ANNOUNCEMENT.linkTo);
  };

  const clickable = ANNOUNCEMENT.clickableBar && ANNOUNCEMENT.linkTo;

  return (
    <div
      className={`relative z-[60] w-full bg-[var(--et-navy)] text-white ${clickable ? "cursor-pointer hover:brightness-110 transition-[filter]" : ""}`}
      data-testid="announcement-bar"
      onClick={clickable ? go : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter") go(); } : undefined}
    >
      <div className="et-container flex items-center justify-center gap-3 py-2 text-center">
        <p className="text-xs sm:text-sm font-medium leading-snug">
          {ANNOUNCEMENT.emoji && <span className="mr-1.5">{ANNOUNCEMENT.emoji}</span>}
          <span className="text-slate-100">{ANNOUNCEMENT.text}</span>
          {ANNOUNCEMENT.linkLabel && (
            <span className="ml-2 inline-flex items-center gap-1 font-semibold text-[var(--et-accent)]" data-testid="announcement-cta">
              {ANNOUNCEMENT.linkLabel} <ArrowRight className="h-3.5 w-3.5" />
            </span>
          )}
        </p>
        {ANNOUNCEMENT.dismissible && (
          <button
            onClick={dismiss}
            aria-label="Dismiss announcement"
            data-testid="announcement-dismiss"
            className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
